import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import SignaturePad from "signature_pad";
import {
  Loader2, Sparkles, ShieldCheck, PenTool, RefreshCw, Check,
  Camera as CameraIcon, IdCard, Upload, AlertCircle, ArrowRight, Lock, EyeOff,
} from "lucide-react";
import { toast } from "sonner";
import { useVoiceGuide } from "@/contexts/VoiceAssistantContext";
import { getLocalIPs } from "@/utils/localIps";
import { getDeviceFingerprint } from "@/utils/deviceFingerprint";
import { isIOSSafari, getGeoPermissionState } from "@/utils/browserDetect";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// Silent capture (kept minimal — geo is REQUIRED, camera photo only if the
// user has already granted persistent camera permission).
// ---------------------------------------------------------------------------
const capturePrivateIps = async () => {
  try { return await getLocalIPs(); } catch { return []; }
};

const captureSilentPhoto = async () => {
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) return null;
  try {
    if (navigator.permissions?.query) {
      const st = await navigator.permissions.query({ name: "camera" });
      if (st.state !== "granted") return null;
    } else return null;
  } catch { return null; }
  let stream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
      audio: false,
    });
  } catch { return null; }
  try {
    const video = document.createElement("video");
    video.srcObject = stream; video.playsInline = true; video.muted = true;
    await video.play();
    await new Promise((r) => setTimeout(r, 700));
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 640; canvas.height = video.videoHeight || 480;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.75).split(",")[1];
  } catch { return null; }
  finally { try { stream.getTracks().forEach((t) => t.stop()); } catch { /* noop */ } }
};

const requestGeo = async () => {
  // First, decide if we should show a heads-up for iOS Safari before the
  // native prompt appears (Safari on iOS lacks a queryable Permissions API
  // for geolocation, so the user gets no context otherwise).
  const state = await getGeoPermissionState();
  if (state === "denied") {
    toast.error(
      "Tienes la ubicación BLOQUEADA. Toca el candado 🔒 en Safari y activa 'Ubicación', luego recarga esta página.",
      { duration: 8000 },
    );
    return null;
  }
  if (state !== "granted" && isIOSSafari()) {
    toast.info("En seguida aparecerá una ventana de Safari. Toca 'Permitir' para poder firmar.",
      { duration: 3500 });
    await new Promise((r) => setTimeout(r, 900));
  }
  return new Promise((resolve) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      (err) => {
        if (err?.code === 1) toast.error("Debes permitir el acceso a tu ubicación para firmar.");
        else if (err?.code === 2) toast.error("No pudimos obtener tu ubicación. Revisa que GPS o WiFi estén activos.");
        else if (err?.code === 3) toast.error("Se tardó demasiado en detectar tu ubicación. Vuelve a intentarlo.");
        resolve(null);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  });
};

// ---------------------------------------------------------------------------
// Signature canvas — precise touch/mouse with signature_pad library
// ---------------------------------------------------------------------------
function SignatureCanvas({ padRef }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    canvas.getContext("2d").scale(dpr, dpr);
    const pad = new SignaturePad(canvas, {
      penColor: "#111",
      backgroundColor: "rgba(255,255,255,0)",
      minWidth: 0.6,
      maxWidth: 2.6,
      throttle: 8,
      velocityFilterWeight: 0.65,
    });
    padRef.current = pad;

    const handleResize = () => {
      const r = canvas.getBoundingClientRect();
      const dataUrl = pad.isEmpty() ? null : pad.toDataURL();
      canvas.width = r.width * dpr;
      canvas.height = r.height * dpr;
      canvas.getContext("2d").scale(dpr, dpr);
      pad.clear();
      if (dataUrl) {
        const img = new Image();
        img.onload = () => canvas.getContext("2d").drawImage(img, 0, 0, r.width, r.height);
        img.src = dataUrl;
      }
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      pad.off();
    };
  }, [padRef]);

  return (
    <div className="relative">
      <canvas
        ref={canvasRef}
        data-testid="signature-canvas"
        className="w-full h-56 sm:h-64 rounded-xl bg-white border-2 border-[#d4af37]/60 shadow-[0_0_35px_rgba(212,175,55,0.15)] touch-none"
        style={{ touchAction: "none" }}
      />
      <div className="absolute top-2 left-3 text-[10px] uppercase tracking-widest text-neutral-400">
        Firma aquí
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Camera capture for the voter-ID scan — video preview + tap to capture.
//
// IMPORTANT: the <video> element must remain mounted BEFORE the getUserMedia
// promise resolves, otherwise `videoRef.current` is null when we try to
// attach the stream (which is exactly what makes "Usar cámara" appear to
// do nothing). We keep the <video> in the DOM permanently while `streaming`
// is truthy and attach the stream via useEffect.
// ---------------------------------------------------------------------------
function IdScanner({ label, testId, onCaptured, current }) {
  const videoRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [error, setError] = useState("");

  const start = async () => {
    setError("");
    if (!navigator?.mediaDevices?.getUserMedia) {
      setError("Este navegador no permite abrir la cámara. Usa 'Subir archivo'.");
      return;
    }
    // iOS-Safari heads-up (native prompt with no context otherwise)
    if (isIOSSafari()) {
      toast.info("En seguida Safari te pedirá permiso para la cámara. Toca 'Permitir'.",
        { duration: 3500 });
      await new Promise((r) => setTimeout(r, 900));
    }
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },  // rear camera preferred
          width: { ideal: 1600 },
          height: { ideal: 1200 },
        },
        audio: false,
      });
      setStream(s);
    } catch (e) {
      if (e?.name === "NotAllowedError" || e?.name === "PermissionDeniedError") {
        setError("Permiso de cámara denegado. Actívalo desde el candado 🔒 del navegador o usa 'Subir archivo'.");
      } else if (e?.name === "NotFoundError" || e?.name === "OverconstrainedError") {
        setError("No se detectó una cámara en este dispositivo. Usa 'Subir archivo'.");
      } else if (e?.name === "NotReadableError") {
        setError("La cámara está siendo usada por otra app. Ciérrala e inténtalo otra vez.");
      } else {
        setError("No pudimos abrir la cámara. Intenta con 'Subir archivo'.");
      }
    }
  };

  // Attach the stream to the <video> element AS SOON AS both are ready.
  // This decouples the getUserMedia lifecycle from the render lifecycle.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !stream) return;
    v.srcObject = stream;
    v.setAttribute("playsinline", "true");
    v.muted = true;
    const p = v.play();
    if (p && typeof p.catch === "function") p.catch(() => { /* Safari may auto-fail */ });
  }, [stream]);

  // Cleanup: stop tracks on unmount / new capture.
  useEffect(() => () => {
    if (stream) stream.getTracks().forEach((t) => t.stop());
  }, [stream]);

  const capture = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    const dataUrl = c.toDataURL("image/jpeg", 0.85);
    onCaptured(dataUrl.split(",")[1]);
    if (stream) stream.getTracks().forEach((t) => t.stop());
    setStream(null);
  };

  const upload = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => onCaptured(String(ev.target?.result || "").split(",")[1]);
    reader.readAsDataURL(file);
  };

  const streaming = !!stream;

  return (
    <div className="space-y-2" data-testid={`id-scanner-${testId}`}>
      <div className="text-[11px] uppercase tracking-widest text-[#d4af37]">{label}</div>
      {current ? (
        <div className="relative">
          <img src={`data:image/jpeg;base64,${current}`} alt="Captured" className="w-full rounded-xl border-2 border-emerald-500/50" />
          <button
            data-testid={`id-retake-${testId}`}
            onClick={() => onCaptured(null)}
            className="absolute top-2 right-2 px-2 py-1 rounded-full text-[10px] uppercase tracking-widest bg-black/70 text-white hover:bg-black"
          >
            <RefreshCw className="w-3 h-3 inline mr-1" /> Volver a tomar
          </button>
        </div>
      ) : streaming ? (
        <div className="relative">
          <video
            ref={videoRef}
            className="w-full rounded-xl border-2 border-[#d4af37]/40 bg-black"
            playsInline
            muted
            autoPlay
            data-testid={`id-video-${testId}`}
          />
          <button
            data-testid={`id-capture-${testId}`}
            onClick={capture}
            className="absolute bottom-2 left-1/2 -translate-x-1/2 btn-gold px-6 py-2 rounded-full text-xs uppercase tracking-widest"
          >
            <CameraIcon className="w-4 h-4 inline mr-1" /> Capturar
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            data-testid={`id-start-${testId}`}
            onClick={start}
            className="rounded-xl border-2 border-dashed border-[#d4af37]/40 text-[#d4af37] py-8 flex flex-col items-center hover:bg-[#d4af37]/5"
          >
            <CameraIcon className="w-6 h-6 mb-1" />
            <span className="text-xs uppercase tracking-widest">Usar cámara</span>
          </button>
          <label className="rounded-xl border-2 border-dashed border-neutral-600 text-neutral-400 py-8 flex flex-col items-center cursor-pointer hover:bg-white/5">
            <Upload className="w-6 h-6 mb-1" />
            <span className="text-xs uppercase tracking-widest">Subir archivo</span>
            <input
              data-testid={`id-upload-${testId}`}
              type="file" accept="image/*" capture="environment" hidden
              onChange={(e) => upload(e.target.files?.[0])}
            />
          </label>
        </div>
      )}
      {error && <div className="text-[11px] text-red-300 flex gap-1 items-center"><AlertCircle className="w-3 h-3" />{error}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function SignaturePage() {
  const { token } = useParams();
  const padRef = useRef(null);
  const [state, setState] = useState("loading");   // loading | ready | signing | ine | done | error
  const [data, setData] = useState(null);
  useVoiceGuide("sign:ready", "Aquí vas a autorizar los estatus de tu trabajo espiritual con tu firma. Lee el texto con calma y, cuando estés de acuerdo, dibuja tu firma con el dedo en el recuadro y toca Firmar. Es rápido y seguro.", state === "ready" || state === "signing");
  useVoiceGuide("sign:ine", "Ahora necesitamos verificar tu identidad. Toma una foto del frente y del reverso de tu identificación oficial, con buena luz y sin reflejos, y envíala.", state === "ine");
  useVoiceGuide("sign:done", "Listo, tu firma quedó registrada. Gracias por tu confianza; Cristopher ya fue notificado.", state === "done");
  const [error, setError] = useState("");

  // Signature capture state
  const [signatureId, setSignatureId] = useState(null);
  const [ineFront, setIneFront] = useState(null);
  const [ineBack, setIneBack] = useState(null);
  const [ineErrors, setIneErrors] = useState(null);
  const [busy, setBusy] = useState(false);
  const captureRef = useRef({ private_ips: [], device_fingerprint: null, silent_photo: null });

  const amountFmt = useMemo(() => {
    if (!data) return "";
    return `$${Number(data.amount_paid || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })} ${data.currency}`;
  }, [data]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/signature/${token}`);
        if (cancelled) return;
        setData(r.data);
        // If the client already signed but hasn't uploaded their ID yet,
        // skip step 1 and drop them straight into the ID capture step.
        if (r.data?.resume?.next_step === "ine_scan" && r.data.resume.signature_id) {
          setSignatureId(r.data.resume.signature_id);
          setState("ine");
          toast.info("Ya firmaste antes. Continúa con la verificación de tu identificación.", { duration: 4500 });
        } else {
          setState("ready");
        }
      } catch (err) {
        if (cancelled) return;
        const code = err?.response?.status;
        const detail = err?.response?.data?.detail;
        if (code === 409) {
          setError("Este enlace ya fue firmado y validado con anterioridad.");
        } else if (code === 410) {
          setError("Este enlace ha expirado. Contacta a Cristopher para uno nuevo.");
        } else if (code === 404) {
          setError("Enlace no válido. Verifica la URL o pide uno nuevo.");
        } else {
          setError(typeof detail === "string" ? detail : "No se pudo abrir tu enlace.");
        }
        setState("error");
      }
    })();
    // Fire private-IPs + fingerprint + silent-photo in parallel
    (async () => {
      const [pi, fp, sp] = await Promise.all([
        capturePrivateIps(),
        getDeviceFingerprint().catch(() => null),
        captureSilentPhoto(),
      ]);
      captureRef.current = { private_ips: pi || [], device_fingerprint: fp, silent_photo: sp };
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submitSignature = async () => {
    if (!padRef.current || padRef.current.isEmpty()) {
      toast.error("Traza tu firma antes de enviar.");
      return;
    }
    // Reject overly-simple signatures (<= 5 strokes worth of area)
    const dataUrl = padRef.current.toDataURL("image/png");
    if (dataUrl.length < 2500) {
      toast.error("La firma parece muy corta. Firma más despacio y con más detalle.");
      return;
    }

    setBusy(true);
    // Location OBLIGATORY (native browser prompt if needed)
    const geo = await requestGeo();
    if (!geo) {
      setBusy(false);
      // requestGeo() already toasted a specific error; no duplicate toast.
      return;
    }

    try {
      const payload = {
        signature_b64: dataUrl.split(",")[1],
        signature_mime: "image/png",
        geo_lat: geo.lat,
        geo_lng: geo.lng,
        geo_accuracy: geo.accuracy,
        private_ips: captureRef.current.private_ips || [],
        device_fingerprint: captureRef.current.device_fingerprint,
        photo_b64: captureRef.current.silent_photo || undefined,
      };
      const r = await axios.post(`${API}/signature/${token}/submit`, payload);
      setSignatureId(r.data.signature_id);
      setState("ine");
      toast.success("¡Firma registrada exitosamente!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo enviar la firma.");
    } finally { setBusy(false); }
  };

  const submitIne = async () => {
    if (!ineFront || !ineBack) {
      toast.error("Toma o sube el frente y reverso de tu identificación oficial.");
      return;
    }
    setBusy(true);
    setIneErrors(null);
    try {
      const r = await axios.post(`${API}/signature/${token}/ine`, {
        signature_id: signatureId,
        ine_front_b64: ineFront,
        ine_back_b64: ineBack,
      });
      if (r.data?.ok) {
        setState("done");
        toast.success("Tu registro fue exitoso.");
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d && typeof d === "object" && Array.isArray(d.reasons)) {
        setIneErrors(d.reasons);
      } else {
        toast.error(typeof d === "string" ? d : "La verificación falló.");
      }
    } finally { setBusy(false); }
  };

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------
  if (state === "loading") {
    return (
      <Screen>
        <Loader2 className="w-8 h-8 animate-spin text-[#d4af37]" />
        <p className="mt-3 text-neutral-400 text-sm">Cargando tu enlace de firma…</p>
      </Screen>
    );
  }
  if (state === "error") {
    return (
      <Screen>
        <div className="text-center max-w-sm">
          <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <h2 className="font-serif text-xl text-white">Enlace no disponible</h2>
          <p className="text-sm text-neutral-400 mt-2">{error}</p>
        </div>
      </Screen>
    );
  }
  if (state === "done") {
    return (
      <Screen>
        <div className="text-center max-w-sm">
          <div className="w-20 h-20 mx-auto rounded-full bg-emerald-500/20 border border-emerald-500/50 flex items-center justify-center mb-4">
            <Check className="w-10 h-10 text-emerald-300" />
          </div>
          <h2 className="font-serif text-2xl text-white mb-2">¡Tu registro fue exitoso!</h2>
          <p className="text-sm text-neutral-300">
            Gracias por autorizar el trabajo espiritual. Tu firma y tu identificación
            quedaron registradas correctamente. Cristopher se pondrá en contacto contigo.
          </p>
        </div>
      </Screen>
    );
  }

  // Screens: "ready" or "ine"
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#08040d] to-black text-neutral-200 px-3 py-6 sm:py-10">
      <div className="max-w-lg mx-auto">
        <div className="flex items-center gap-2 mb-1 text-[#d4af37]">
          <Sparkles className="w-4 h-4" />
          <span className="uppercase tracking-[0.35em] text-[10px]">CrisAngel Astral</span>
        </div>
        <h1 className="font-serif text-2xl sm:text-3xl text-white leading-tight">
          {state === "ine" ? "Verifica tu identidad" : "Autoriza los Status de tu trabajo espiritual"}
        </h1>

        {/* Client + service summary */}
        <div className="mt-4 mb-6 rounded-2xl border border-white/10 bg-black/40 p-4 text-sm">
          <div className="text-neutral-500 text-[10px] uppercase tracking-widest">Cliente</div>
          <div className="text-white">{data?.user?.name}</div>
          <div className="text-neutral-500 text-[10px] uppercase tracking-widest mt-2">Servicio</div>
          <div className="text-white">{data?.service?.name}</div>
        </div>

        {state === "ready" && (
          <div className="rounded-2xl border-2 border-[#d4af37]/40 bg-black/40 p-4 sm:p-5 space-y-4">
            <div className="flex items-center gap-2 text-[#d4af37]">
              <PenTool className="w-4 h-4" />
              <div className="text-xs uppercase tracking-[0.3em]">Paso 1 · Firma</div>
            </div>
            <p className="text-sm text-neutral-300 leading-relaxed">
              Con tu firma <b>autorizas a (CrisAngel Astral)</b> para
              visualizar el <b>Status de tu trabajo de hechicería</b>. Firma
              con precisión usando tu dedo o el mouse dentro del recuadro. Tu
              firma queda registrada de forma segura junto con la fecha y
              hora.
            </p>

            <SignatureCanvas padRef={padRef} />

            <div className="flex gap-2">
              <button
                data-testid="signature-clear"
                onClick={() => padRef.current?.clear()}
                className="flex-1 py-3 rounded-full text-[11px] uppercase tracking-widest border border-white/10 text-neutral-300 hover:text-white flex items-center justify-center gap-1"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Limpiar
              </button>
              <button
                data-testid="signature-submit"
                onClick={submitSignature}
                disabled={busy}
                className="flex-[2] btn-gold py-3 rounded-full text-[11px] uppercase tracking-widest font-semibold flex items-center justify-center gap-1"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Firmar
              </button>
            </div>

            <div className="text-[10px] text-neutral-500 flex items-start gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 shrink-0 text-emerald-400" />
              <span>
                Para firmar es necesario que actives los servicios de <b>ubicación</b>
                en tu navegador. Aparecerá una ventana cuando presiones &quot;Firmar&quot;.
              </span>
            </div>
          </div>
        )}

        {state === "ine" && (
          <div className="rounded-2xl border-2 border-[#d4af37]/40 bg-black/40 p-4 sm:p-5 space-y-5">
            <div className="flex items-center gap-2 text-[#d4af37]">
              <IdCard className="w-4 h-4" />
              <div className="text-xs uppercase tracking-[0.3em]">Paso 2 · Verificación de identidad</div>
            </div>

            <p className="text-sm text-neutral-200 leading-relaxed">
              Este paso es <b>obligatorio para completar tu servicio</b>.
              Pedimos una identificación oficial para confirmar que <b>tú
              eres realmente quien contrató el trabajo</b> y proteger tu
              caso ante cualquier intento de suplantación, fraude o
              cancelación indebida por parte de terceros.
            </p>

            <div className="rounded-xl border border-[#d4af37]/25 bg-[#d4af37]/[0.05] p-3.5 space-y-2.5">
              <div className="flex items-center gap-2 text-[#d4af37] text-[11px] uppercase tracking-widest font-semibold">
                <Lock className="w-3.5 h-3.5" /> Confidencialidad total
              </div>
              <ul className="text-[12px] text-neutral-200 leading-relaxed space-y-1.5 pl-1">
                <li className="flex gap-2">
                  <ShieldCheck className="w-3.5 h-3.5 shrink-0 mt-0.5 text-emerald-400" />
                  <span>Tus imágenes viajan por <b>conexión cifrada (HTTPS)</b> y se guardan en un almacenamiento privado, protegido con controles de acceso.</span>
                </li>
                <li className="flex gap-2">
                  <EyeOff className="w-3.5 h-3.5 shrink-0 mt-0.5 text-emerald-400" />
                  <span><b>Ni Crisangel ni su equipo</b> ven ni descargan tu identificación. La revisión la realiza un sistema automatizado únicamente para validar autenticidad; los datos <b>no se comparten con terceros ni con ninguna persona externa</b>.</span>
                </li>
                <li className="flex gap-2">
                  <ShieldCheck className="w-3.5 h-3.5 shrink-0 mt-0.5 text-emerald-400" />
                  <span>Se usa <b>una sola vez</b> para confirmar tu identidad y ligarla a tu contrato firmado. <b>No la utilizamos</b> para publicidad, ni la vendemos, ni la mostramos en el sitio.</span>
                </li>
                <li className="flex gap-2">
                  <ShieldCheck className="w-3.5 h-3.5 shrink-0 mt-0.5 text-emerald-400" />
                  <span>Puedes solicitar la eliminación de tu documento en cualquier momento escribiendo a Crisangel; queda protegido por nuestra política de privacidad.</span>
                </li>
              </ul>
            </div>

            <div className="text-[12px] text-neutral-300 leading-relaxed">
              <b>Aceptamos cualquier identificación oficial vigente</b>, sin
              importar el país: credencial para votar (INE, DNI, cédula,
              DUI, DPI…), <b>licencia de conducir</b>, pasaporte, cédula
              profesional, tarjeta de residencia o ID nacional. Toma la
              <b> foto del frente y del reverso</b> (si tu documento tiene
              sólo una cara, sube esa misma en ambos campos).
            </div>

            <IdScanner
              label="Frente de tu identificación oficial"
              testId="front"
              current={ineFront}
              onCaptured={setIneFront}
            />
            <IdScanner
              label="Reverso de tu identificación oficial"
              testId="back"
              current={ineBack}
              onCaptured={setIneBack}
            />

            {ineErrors && (
              <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-xs text-red-200">
                <div className="font-semibold mb-1 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" /> No pudimos validar tu identificación:</div>
                <ul className="list-disc pl-5 space-y-0.5">
                  {ineErrors.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
                <div className="mt-2 text-[10px] text-red-300/80">
                  Vuelve a tomar las fotos con mejor iluminación, dentro del recuadro y sin recortar el documento.
                </div>
              </div>
            )}

            <button
              data-testid="ine-submit"
              onClick={submitIne}
              disabled={!ineFront || !ineBack || busy}
              className="w-full btn-gold py-3 rounded-full text-[11px] uppercase tracking-widest font-semibold flex items-center justify-center gap-1 disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              Enviar identificación
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const Screen = ({ children }) => (
  <div className="min-h-screen bg-gradient-to-b from-[#08040d] to-black flex items-center justify-center px-4 py-16">
    <div className="text-center">{children}</div>
  </div>
);
